import { LitElement, css, html, nothing, unsafeCSS } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import sheet from './css/bb-json.css?inline'

// Collapsible JSON tree. Renders any value as a click-to-toggle tree:
// primitives show inline; objects/arrays show `▸ { 3 }` collapsed or
// `▾ { … }` expanded with children indented. Each node owns its own
// expanded state, keyed by a dotted path from the root.
//
// `default-depth` controls how deep the tree is open on first render:
// 0 = root collapsed, 1 = root open / children collapsed (the default),
// Infinity = everything open.

@customElement('bb-json')
export class BbJson extends LitElement {
  static styles = css`${unsafeCSS(sheet)}`

  @property({ attribute: false })
  value: unknown = undefined

  @property({ attribute: 'default-depth', type: Number })
  defaultDepth = 1

  // Tracks which paths the user has *flipped* from the default-depth
  // baseline. A path present here means "the opposite of what the depth
  // rule says" — so re-rendering with new `value` doesn't lose state and
  // we don't have to pre-populate every path on mount.
  @state() private flipped = new Set<string>()

  private isOpen(path: string, depth: number): boolean {
    const baseline = depth < this.defaultDepth
    return this.flipped.has(path) ? !baseline : baseline
  }

  private toggle(path: string) {
    const next = new Set(this.flipped)
    if (next.has(path)) next.delete(path)
    else next.add(path)
    this.flipped = next
  }

  private renderPrimitive(v: unknown) {
    if (v === null) return html`<span class="null">null</span>`
    if (typeof v === 'string')
      return html`<span class="string">${JSON.stringify(v)}</span>`
    if (typeof v === 'number')
      return html`<span class="number">${String(v)}</span>`
    if (typeof v === 'boolean')
      return html`<span class="bool">${String(v)}</span>`
    return html`<span>${String(v)}</span>`
  }

  private renderNode(
    v: unknown,
    path: string,
    depth: number,
    trailingComma: boolean,
  ): unknown {
    const comma = trailingComma ? html`<span class="muted">,</span>` : nothing
    if (
      v === null ||
      typeof v === 'string' ||
      typeof v === 'number' ||
      typeof v === 'boolean' ||
      typeof v === 'undefined'
    ) {
      return html`${this.renderPrimitive(v)}${comma}`
    }
    if (Array.isArray(v)) {
      const open = this.isOpen(path, depth)
      const count = v.length
      const caret = html`<span
        class="caret"
        @click=${(e: Event) => {
          e.stopPropagation()
          this.toggle(path)
        }}
        >${open ? '▾' : '▸'}</span
      >`
      if (!open) {
        return html`<span class="row summary" @click=${() => this.toggle(path)}
          >${caret}<span class="muted">[ ${count} ]</span>${comma}</span
        >`
      }
      const items = v.map((child, i) =>
        html`<div class="row">${this.renderNode(child, `${path}.${i}`, depth + 1, i < count - 1)}</div>`,
      )
      return html`<span class="row"
          >${caret}<span class="muted">[</span></span
        >
        <div class="children">${items}</div>
        <div class="row"><span class="muted">]</span>${comma}</div>`
    }
    // plain object
    const entries = Object.entries(v as Record<string, unknown>)
    const open = this.isOpen(path, depth)
    const caret = html`<span
      class="caret"
      @click=${(e: Event) => {
        e.stopPropagation()
        this.toggle(path)
      }}
      >${open ? '▾' : '▸'}</span
    >`
    if (!open) {
      const preview = entries.length
        ? html`<span class="muted">{ ${entries.length} }</span>`
        : html`<span class="muted">{}</span>`
      return html`<span class="row summary" @click=${() => this.toggle(path)}
        >${caret}${preview}${comma}</span
      >`
    }
    const rows = entries.map(([k, child], i) =>
      html`<div class="row">
        <span class="key">${JSON.stringify(k)}</span><span class="muted">: </span>${this.renderNode(
          child,
          `${path}.${k}`,
          depth + 1,
          i < entries.length - 1,
        )}
      </div>`,
    )
    return html`<span class="row"
        >${caret}<span class="muted">{</span></span
      >
      <div class="children">${rows}</div>
      <div class="row"><span class="muted">}</span>${comma}</div>`
  }

  render() {
    return html`<div class="root">
      ${this.renderNode(this.value, '$', 0, false)}
    </div>`
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'bb-json': BbJson
  }
}
